/**
 * ChatController: уся логіка чату без прив'язки до React.
 *
 * Інтерфейс лише підписується на стан (subscribe/getState) і викликає методи
 * (start, next, stop, report...). Тому новий дизайн, інший фреймворк чи
 * мобільна обгортка підключаються до тієї самої логіки.
 */
import {
  PROTOCOL_VERSION,
  type ClientMsg,
  type IceServer,
  type ReportReason,
  type ServerMsg,
} from '@rvc/protocol';
import { PeerSession } from './rtc';

export type Status =
  | 'idle' // ще не починали
  | 'starting' // просимо доступ до камери
  | 'connecting' // з'єднуємося із сервером
  | 'ready' // підключені, але не шукаємо («Стоп»)
  | 'searching' // у черзі
  | 'pairing' // пару знайдено, встановлюємо відеозв'язок
  | 'connected' // йде розмова
  | 'error';

export type ChatError = 'denied' | 'notfound' | 'camera' | 'banned' | 'restricted' | 'replaced' | 'version' | 'server';
export type PeerLeftReason = 'next' | 'leave' | 'disconnect' | 'timeout' | 'banned' | 'restricted';

export interface ChatState {
  status: Status;
  error: ChatError | null;
  waiting: number;
  online: number | null;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  micOn: boolean;
  camOn: boolean;
  pairId: string | null;
  /** Причина, з якої минулий співрозмовник зник (для короткого повідомлення). */
  lastPeerLeft: PeerLeftReason | null;
  reported: boolean;
  /** Незалежно від reported: партнера заблоковано, більше ніколи не зустрінетесь. */
  blocked: boolean;
  rateLimited: boolean;
}

export interface ControllerOptions {
  wsUrl: string;
  lang: string;
}

const TOKEN_KEY = 'rvc.session';
const PAIRING_TIMEOUT_MS = 20_000;

function loadToken(): string | undefined {
  try {
    return localStorage.getItem(TOKEN_KEY) ?? undefined;
  } catch {
    return undefined;
  }
}
function saveToken(token: string): void {
  try {
    localStorage.setItem(TOKEN_KEY, token);
  } catch {
    /* сховище недоступне (приватний режим, iframe): працюємо без нього */
  }
}

function mapMediaError(e: unknown): ChatError {
  const name = (e as { name?: string } | null)?.name;
  if (name === 'NotAllowedError' || name === 'SecurityError') return 'denied';
  if (name === 'NotFoundError' || name === 'OverconstrainedError') return 'notfound';
  return 'camera';
}

export class ChatController {
  private state: ChatState = {
    status: 'idle',
    error: null,
    waiting: 0,
    online: null,
    localStream: null,
    remoteStream: null,
    micOn: true,
    camOn: true,
    pairId: null,
    lastPeerLeft: null,
    reported: false,
    blocked: false,
    rateLimited: false,
  };
  private listeners = new Set<() => void>();

  private ws: WebSocket | null = null;
  private ready = false;
  private attempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pairingTimer: ReturnType<typeof setTimeout> | null = null;
  private wantSearch = false;
  private disposed = false;

  private ice: { iceServers: IceServer[]; policy: 'all' | 'relay'; maxVideoBitrate: number } = {
    iceServers: [],
    policy: 'all',
    maxVideoBitrate: 1_000_000,
  };
  private peer: PeerSession | null = null;

  constructor(private readonly opts: ControllerOptions) {}

  // ------------------------------------------------------------ підписка (для UI)

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getState = (): ChatState => this.state;

  private set(patch: Partial<ChatState>): void {
    this.state = { ...this.state, ...patch };
    for (const fn of this.listeners) fn();
  }

  // ------------------------------------------------------------ дії користувача

  /** «Почати»: доступ до камери → з'єднання з сервером → пошук. */
  async start(): Promise<void> {
    const s = this.state.status;
    if (s === 'starting' || s === 'searching' || s === 'pairing' || s === 'connected') return;
    this.wantSearch = true;
    this.set({ error: null, reported: false, blocked: false, lastPeerLeft: null });

    if (!this.state.localStream) {
      this.set({ status: 'starting' });
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { width: { ideal: 640 }, height: { ideal: 480 }, frameRate: { ideal: 24 }, facingMode: 'user' },
          audio: { echoCancellation: true, noiseSuppression: true },
        });
        if (this.disposed) return stream.getTracks().forEach((t) => t.stop());
        this.set({ localStream: stream, micOn: true, camOn: true });
      } catch (err) {
        this.set({ status: 'error', error: mapMediaError(err) });
        return;
      }
    }

    if (this.ready) {
      this.send({ type: 'join' });
      this.set({ status: 'searching' });
    } else if (!this.ws) {
      this.connect();
    }
  }

  /** «Наступний». */
  next(): void {
    const s = this.state.status;
    if (s !== 'searching' && s !== 'pairing' && s !== 'connected') return;
    this.closePeer();
    this.send({ type: 'next' });
    this.set({
      status: 'searching',
      remoteStream: null,
      pairId: null,
      lastPeerLeft: null,
      reported: false,
      blocked: false,
    });
  }

  /** «Стоп»: виходимо з пошуку, але залишаємось підключеними. */
  stop(): void {
    this.wantSearch = false;
    this.closePeer();
    if (this.ready) this.send({ type: 'leave' });
    this.set({ status: this.ready ? 'ready' : 'idle', remoteStream: null, pairId: null, lastPeerLeft: null });
  }

  /** Скарга на поточного співрозмовника; кадр знімає інтерфейс. Після скарги йдемо далі. */
  report(reason: ReportReason, frameJpeg?: string): void {
    const pairId = this.state.pairId;
    if (!pairId || this.state.status !== 'connected') return;
    this.send({ type: 'report', pairId, reason, ...(frameJpeg ? { frameJpeg } : {}) });
    this.next();
    this.set({ reported: true });
  }

  /**
   * Block — незалежно від Report. Партнер більше ніколи не з'явиться в підборі
   * для цієї сесії (і навпаки), незалежно від того, чи була скарга.
   */
  block(): void {
    const pairId = this.state.pairId;
    if (!pairId || this.state.status !== 'connected') return;
    this.send({ type: 'block', pairId });
    this.next();
    this.set({ blocked: true });
  }

  toggleMic(): void {
    const on = !this.state.micOn;
    this.state.localStream?.getAudioTracks().forEach((t) => (t.enabled = on));
    this.set({ micOn: on });
  }

  toggleCam(): void {
    const on = !this.state.camOn;
    this.state.localStream?.getVideoTracks().forEach((t) => (t.enabled = on));
    this.set({ camOn: on });
  }

  dispose(): void {
    this.disposed = true;
    this.wantSearch = false;
    if (this.reconnectTimer) clearTimeout(this.reconnectTimer);
    this.clearPairingTimer();
    this.closePeer();
    const ws = this.ws;
    this.ws = null;
    ws?.close(1000);
    this.state.localStream?.getTracks().forEach((t) => t.stop());
    this.listeners.clear();
  }

  // ------------------------------------------------------------ з'єднання

  private connect(): void {
    if (this.disposed) return;
    if (this.state.status !== 'searching' && this.state.status !== 'pairing') {
      this.set({ status: 'connecting' });
    }
    const ws = new WebSocket(this.opts.wsUrl);
    this.ws = ws;
    ws.onopen = () => {
      if (ws !== this.ws) return;
      this.attempts = 0;
      this.send({
        type: 'hello',
        v: PROTOCOL_VERSION,
        ageConfirmed: true,
        lang: this.opts.lang,
        ...(loadToken() ? { sessionToken: loadToken()! } : {}),
      });
    };
    ws.onmessage = (e) => {
      if (ws !== this.ws) return;
      try {
        this.onServer(JSON.parse(String(e.data)) as ServerMsg);
      } catch (err) {
        console.warn('bad server message', err);
      }
    };
    ws.onclose = (e) => {
      if (ws === this.ws) this.onClose(e.code);
    };
    ws.onerror = () => undefined; // подробиці приходять у onclose
  }

  private onClose(code: number): void {
    this.ws = null;
    this.ready = false;
    this.closePeer();
    this.set({ remoteStream: null, pairId: null });
    if (this.disposed) return;

    if (code === 4003) return this.fatal('banned');
    if (code === 4006) return this.fatal('restricted');
    if (code === 4005) return this.fatal('replaced');
    if (code === 4004) return this.fatal('version');

    // Тимчасовий розрив: перепідключаємось із наростаючою паузою.
    this.set({ status: 'connecting' });
    const delay = Math.min(10_000, 1000 * 2 ** this.attempts++);
    this.reconnectTimer = setTimeout(() => this.connect(), delay);
  }

  private fatal(error: ChatError): void {
    this.wantSearch = false;
    this.state.localStream?.getTracks().forEach((t) => t.stop());
    this.set({ status: 'error', error, localStream: null });
  }

  private send(msg: ClientMsg): void {
    if (this.ws?.readyState === WebSocket.OPEN) this.ws.send(JSON.stringify(msg));
  }

  // ------------------------------------------------------------ повідомлення сервера

  private onServer(msg: ServerMsg): void {
    switch (msg.type) {
      case 'hello_ack': {
        saveToken(msg.sessionToken);
        this.ice = {
          iceServers: msg.iceServers,
          policy: msg.iceTransportPolicy,
          maxVideoBitrate: msg.maxVideoBitrate,
        };
        this.ready = true;
        this.set({ online: msg.online });
        if (this.wantSearch) {
          this.send({ type: 'join' });
          this.set({ status: 'searching' });
        } else {
          this.set({ status: 'ready' });
        }
        return;
      }
      case 'queued':
        this.set({ status: 'searching', waiting: msg.waiting, rateLimited: false });
        return;
      case 'matched':
        this.startPeer(msg.pairId, msg.role);
        return;
      case 'signal':
        if (msg.pairId === this.state.pairId) void this.peer?.handleSignal(msg.kind, msg.data);
        return;
      case 'peer_left':
        if (msg.pairId && msg.pairId !== this.state.pairId) return;
        this.closePeer();
        // Сервер сам повертає нас у чергу й надішле `queued`.
        this.set({ status: 'searching', remoteStream: null, pairId: null, lastPeerLeft: msg.reason });
        return;
      case 'error':
        if (msg.code === 'rate_limited') this.set({ rateLimited: true });
        else if (msg.code === 'banned') this.fatal('banned');
        else if (msg.code === 'restricted') this.fatal('restricted');
        else if (msg.code === 'bad_version') this.fatal('version');
        else if (msg.code === 'internal') console.warn('server error', msg.message);
        return;
    }
  }

  // ------------------------------------------------------------ дзвінок

  private startPeer(pairId: string, role: 'caller' | 'callee'): void {
    const local = this.state.localStream;
    if (!local) return;
    this.closePeer();
    this.set({
      status: 'pairing',
      pairId,
      remoteStream: null,
      lastPeerLeft: null,
      reported: false,
      blocked: false,
    });

    const peer = new PeerSession(role, this.ice, local, {
      onSignal: (kind, data) => this.send({ type: 'signal', pairId, kind, data }),
      onRemoteStream: (remoteStream) => {
        if (this.peer === peer) this.set({ remoteStream });
      },
      onConnected: (route) => {
        if (this.peer !== peer) return;
        this.clearPairingTimer();
        this.set({ status: 'connected' });
        this.send({ type: 'telemetry', pairId, kind: 'connected', route });
      },
      onFailed: () => {
        if (this.peer !== peer) return;
        this.send({ type: 'telemetry', pairId, kind: 'failed' });
        this.next();
      },
      onStats: (data) => {
        if (this.peer === peer) this.send({ type: 'telemetry', pairId, kind: 'stats', data });
      },
    });
    this.peer = peer;

    this.clearPairingTimer();
    this.pairingTimer = setTimeout(() => {
      if (this.peer === peer && this.state.status === 'pairing') {
        this.send({ type: 'telemetry', pairId, kind: 'failed', data: { reason: 'timeout' } });
        this.next();
      }
    }, PAIRING_TIMEOUT_MS);

    void peer.start().catch((err) => console.warn('peer start failed', err));
  }

  private closePeer(): void {
    this.clearPairingTimer();
    this.peer?.close();
    this.peer = null;
  }

  private clearPairingTimer(): void {
    if (this.pairingTimer) clearTimeout(this.pairingTimer);
    this.pairingTimer = null;
  }
}
